import React, { Suspense } from "react";

import "../hassibot/translations";
import "./alternative-entrypoints/service-worker/client";
import "./custom-pwa-compat";

import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from "@azure/msal-react";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { detect, OperatingSystem } from "detect-browser";

import { initAmplitude } from "@config/amplitude";
import appConfig from "@config/app";
import { initDatadogRum } from "@config/datadog";
import { initSentryWebApp } from "@config/sentry";
import { ohQueryClient } from "@shared/libs/react-query/query-client";
import { MaintenancePage } from "hassibot/component/maintenance-mode/MaintenancePage";
import { CurrentLegalEntityProvider } from "hassibot/current-legal-entity-provider";
import { useAuthSync } from "hassibot/services_v2/login/hooks/use-auth-sync";
import { useDeviceRegistration } from "hassibot/services_v2/login/hooks/use-device-registration";
import { msalInstance } from "hassibot/services_v2/login/msal";
import { useFollowServiceWorker } from "hassibot/services_v2/service-worker";
import "scss/main.scss";
import { mtLogger } from "shared-core";
import { HassibotError } from "shared-core/custom-error";

import { SessionContainer } from "./inside-components";
import {
  BadRequest,
  HoustonWeveGotAProblem,
  Loading,
  LoginStandaloneApp,
  OldChromeBrowser,
  OldSafariBrowser,
  The404,
  UnsupportedBrowser,
  WrongBrowserOnMobile,
} from "./outside-components";

mtLogger.log(`Welcome to Hassibot! I'm ${appConfig.BUILD_HASH}`);

if (import.meta.env.VITE_SENTRY_DSN) {
  initSentryWebApp();
}

const isProduction = appConfig.IS_DEV === false && appConfig.IS_TEST_ENVS === false;

if (isProduction) {
  initDatadogRum();
  initAmplitude(appConfig.DEVICE_ID);
}

// Start our shared worker early to give it a head start. Beware, as of
// 2023-11-16 this does not work an Android (see following refs). We
// fallback to "regular worker" there, with an abvious performance
// cost. Ref:
//   - https://caniuse.com/sharedworkers
//   - https://bugs.chromium.org/p/chromium/issues/detail?id=154571
window.SharedWorker &&
  new SharedWorker(new URL("./alternative-entrypoints/webworker", import.meta.url), {
    type: "module",
  });

/**
 * Functional component that uses the device registration hook and renders the main shell.
 * Separated to allow Hook usage while keeping InsideContainer as an error boundary.
 */
const InsideContainer = () => {
  useDeviceRegistration();

  const ShellContainer = React.lazy(() => import("./main"));
  const GlobalStyle = React.lazy(() => import("../styles"));

  return (
    <Suspense fallback={<Loading />}>
      <SessionContainer>
        <CurrentLegalEntityProvider>
          <GlobalStyle />
          <ShellContainer />
        </CurrentLegalEntityProvider>
      </SessionContainer>
    </Suspense>
  );
};

type ErrorBoundaryContainerState = {
  errorType?: number;
};

/**
 * Error boundary for the authenticated part of the application.
 * This is rendered after successful authentication and catches any fatal errors that occur within the app.
 * It displays appropriate error pages based on the error type (400, 404, or generic 520).
 */
class ErrorBoundaryContainer extends React.PureComponent<{}, ErrorBoundaryContainerState> {
  constructor(props) {
    super(props);
    this.state = { errorType: undefined };
  }

  componentDidCatch(error, errorInfo) {
    const errorType = error.type || 520;
    console.log("errorType-----", errorType);
    this.setState({ errorType });

    // Do not trigger Sentry for a 400 & 404
    if ([400, 404].includes(errorType)) {
      return;
    }

    Sentry.withScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key]);
      });

      if (error instanceof HassibotError) {
        if (error.additionalData) {
          const additionalData = error.additionalData;
          Object.keys(additionalData).forEach(key => {
            scope.setExtra(key, additionalData[key]);
          });
        }
      }

      Sentry.captureException(error);
    });
  }

  errorTypeToComponent = (errorType: number) => {
    switch (true) {
      case errorType === 400:
        return BadRequest;
      case errorType === 404:
        return The404;
      default:
        return HoustonWeveGotAProblem;
    }
  };

  render() {
    const { errorType } = this.state;

    const ErrorComponent = errorType ? this.errorTypeToComponent(errorType) : null;
    // Error boundaries
    if (ErrorComponent) {
      return <ErrorComponent />;
    }

    return <InsideContainer />;
  }
}

/**
 * Container that handles authentication state with MSAL.
 * Shows login page if not authenticated, otherwise shows the app.
 */
const UserStateContainer = () => {
  useFollowServiceWorker();
  useAuthSync();

  return (
    <MsalProvider instance={msalInstance}>
      <UnauthenticatedTemplate>
        <LoginStandaloneApp />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <QueryClientProvider client={ohQueryClient}>
          <ErrorBoundaryContainer />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AuthenticatedTemplate>
    </MsalProvider>
  );
};

const biggerThan92DotX = (version: string) => {
  const [majorVersion, _minorVersion, _patchVersion] = version.split(".");
  if (!majorVersion) {
    throw new Error(`Invalid version: major=${majorVersion}`);
  }
  return parseInt(majorVersion) >= 92;
};

const biggerThan15Dot4 = (version: string) => {
  const [majorVersion, minorVersion] = version.split(".");
  if (!majorVersion || !minorVersion) {
    throw new Error(`Invalid version: major=${majorVersion} minor=${minorVersion}`);
  }
  return (
    parseInt(majorVersion) > 15 || (parseInt(majorVersion) === 15 && parseInt(minorVersion) >= 4)
  );
};

// Check if Hassibot can run in the current browser.
const TestBrowserContainer = () => {
  const browserInfos = detect();
  const browser =
    browserInfos && browserInfos.name && browserInfos.version && browserInfos.os
      ? {
          name: browserInfos.name,
          version: browserInfos.version,
          os: browserInfos.os as OperatingSystem,
        }
      : undefined;

  const browserIsChrome = browser ? browser.name === "chrome" : undefined;
  const isOldChromeBrowser = browser
    ? browserIsChrome && !biggerThan92DotX(browser.version)
    : undefined;

  const browserIsSafari = browser ? browser.name === "safari" || browser.name === "ios" : undefined;
  const isOldSafariBrowser = browser
    ? browserIsSafari && !biggerThan15Dot4(browser.version)
    : undefined;

  const isWrongBrowserOnMobile = browser
    ? (browser.os === "Android OS" && browser.name !== "chrome") ||
      (browser.os === "iOS" && !browserIsSafari)
    : undefined;
  if (browser && isWrongBrowserOnMobile) {
    return <WrongBrowserOnMobile infos={browserInfos} platform={browser.os} />;
  } else if (browser && isOldChromeBrowser) {
    return <OldChromeBrowser browserOs={browser.os} />;
  } else if (browser && isOldSafariBrowser) {
    return <OldSafariBrowser browserOs={browser.os} />;
  } else if (
    !window.WebSocket ||
    !navigator.serviceWorker ||
    !Object.fromEntries ||
    !window.BroadcastChannel
  ) {
    return <UnsupportedBrowser />;
  } else {
    return <UserStateContainer />;
  }
};

// Welcome to Hassibot!
export const Hassibot = () => {
  if (appConfig.IS_MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return <TestBrowserContainer />;
};
